/**
 * Tone.js-compatible wrapper for WASM FM Synth
 * Makes FMSynthBridge compatible with existing Tone.js code
 */

import * as Tone from 'tone';
import { FMSynthBridge } from './fm-synth-bridge';

export class WASMFMSynth {
  private bridge: FMSynthBridge;
  private gainNode: GainNode;
  private currentNote: number | null = null;
  private _detuneValue: number = 0;

  // Tone.js-compatible properties
  public volume: {
    value: number;
    rampTo: (value: number, rampTime: number, startTime?: number) => void;
  };
  public detune: { value: number };
  public portamento: number = 0;

  constructor(context: AudioContext) {
    this.bridge = new FMSynthBridge(context);

    // Create gain node for volume control
    this.gainNode = context.createGain();
    this.gainNode.gain.value = 1.0;

    // Tone.js-compatible volume object with rampTo support
    const gainNode = this.gainNode;
    this.volume = {
      get value() {
        return Tone.gainToDb(gainNode.gain.value);
      },
      set value(db: number) {
        const gain = Tone.dbToGain(db);
        gainNode.gain.value = gain;
      },
      rampTo(db: number, rampTime: number, startTime?: number) {
        const gain = Tone.dbToGain(db);
        const when = startTime ?? context.currentTime;
        gainNode.gain.linearRampToValueAtTime(gain, when + rampTime);
      }
    };

    // Tone.js-compatible detune object
    const self = this;
    this.detune = {
      get value() {
        return self._detuneValue;
      },
      set value(cents: number) {
        self._detuneValue = cents;
        self.bridge.setDetune(cents);
      }
    };
  }

  async initialize(): Promise<void> {
    await this.bridge.initialize();
    // Connect bridge to gain node
    this.bridge.connect(this.gainNode);
  }

  // Tone.js-compatible method
  triggerAttack(note: string | number, _time?: number, velocity: number = 1.0): void {
    // Convert note to MIDI number if string
    const midiNote = typeof note === 'string'
      ? Tone.Frequency(note).toMidi()
      : note;

    // Convert velocity (0-1) to MIDI velocity (0-127)
    const midiVelocity = Math.floor(velocity * 127);

    this.currentNote = Math.round(midiNote);
    this.bridge.noteOn(this.currentNote, midiVelocity);
  }

  // Tone.js-compatible method
  triggerRelease(_time?: number): void {
    this.bridge.noteOff();
    this.currentNote = null;
  }

  // Tone.js-compatible method
  triggerAttackRelease(
    note: string | number,
    duration: string | number,
    time?: number,
    velocity: number = 1.0
  ): void {
    // Trigger attack immediately
    this.triggerAttack(note, time, velocity);

    // Calculate release time
    const durationSeconds = typeof duration === 'string'
      ? Tone.Time(duration).toSeconds()
      : duration;

    // Schedule release using setTimeout
    // WASM synth doesn't use Transport - notes are sent directly to AudioWorklet
    setTimeout(() => {
      this.triggerRelease();
    }, durationSeconds * 1000);
  }

  // Connect to Tone.js destination or other nodes
  connect(destination: AudioNode | Tone.ToneAudioNode): this {
    if (destination instanceof AudioNode) {
      this.gainNode.connect(destination);
    } else {
      // Tone.js node - connect to its input
      this.gainNode.connect(destination.input || destination as any);
    }
    return this;
  }

  disconnect(): void {
    this.gainNode.disconnect();
  }

  dispose(): void {
    this.disconnect();
    this.bridge.dispose();
  }

  // Tone.js-compatible method - cancel all scheduled events
  // For WASM synth, we don't use Transport scheduling, so this is a no-op
  unsync(): this {
    // No scheduled events to cancel in WASM synth
    // Notes are triggered directly, not scheduled on Transport
    return this;
  }

  // Expose WASM-specific controls
  get wasmBridge(): FMSynthBridge {
    return this.bridge;
  }

  // Helper to check if this is a WASM synth
  isWASMSynth(): boolean {
    return true;
  }
}
